import { createFileRoute } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, subDays, subWeeks, subMonths, parseISO, isBefore } from 'date-fns';
import {
  Box,
  Tabs,
  TabList,
  Tab,
} from '@mui/joy';
import { DashboardCharts } from '@/components/Pages/Dashboard/DashboardCharts';
import type { PeriodType } from '@/types/statistics';

const dashboardFormSchema = z
  .object({
    dateFrom: z.string().min(1, 'La fecha desde es requerida'),
    dateTo: z.string().min(1, 'La fecha hasta es requerida'),
  })
  .refine(
    data => {
      if (!data.dateFrom || !data.dateTo) return true;
      const fromDate = parseISO(data.dateFrom);
      const toDate = parseISO(data.dateTo);
      return !isBefore(toDate, fromDate);
    },
    {
      message: 'La fecha hasta no puede ser anterior a la fecha desde',
      path: ['dateTo'],
    }
  );

type DashboardFormData = z.infer<typeof dashboardFormSchema>;

export const Route = createFileRoute('/_protected/dashboard')({
  component: DashboardPage,
  head: () => pageTitle('Dashboard'),
});

function DashboardPage() {
  const [periodType, setPeriodType] = useState<PeriodType>('daily');

  const getDefaultDates = (type: PeriodType) => {
    const today = new Date();
    switch (type) {
      case 'daily':
        return {
          dateFrom: format(subDays(today, 30), 'yyyy-MM-dd'),
          dateTo: format(today, 'yyyy-MM-dd'),
        };
      case 'weekly':
        return {
          dateFrom: format(subWeeks(today, 12), 'yyyy-MM-dd'),
          dateTo: format(today, 'yyyy-MM-dd'),
        };
      case 'monthly':
        return {
          dateFrom: format(subMonths(today, 12), 'yyyy-MM-dd'),
          dateTo: format(today, 'yyyy-MM-dd'),
        };
    }
  };

  const defaultDates = useMemo(() => getDefaultDates(periodType), [periodType]);

  const {
    watch,
    reset,
  } = useForm<DashboardFormData>({
    resolver: zodResolver(dashboardFormSchema),
    defaultValues: defaultDates,
  });

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  const handlePeriodChange = (newPeriod: PeriodType) => {
    setPeriodType(newPeriod);
    const newDates = getDefaultDates(newPeriod);
    reset(newDates);
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={periodType}
          onChange={(_, value) => handlePeriodChange(value as PeriodType)}
        >
          <TabList>
            <Tab value='daily'>Diario</Tab>
            <Tab value='weekly'>Semanal</Tab>
            <Tab value='monthly'>Mensual</Tab>
          </TabList>
        </Tabs>
      </Box>

      {dateFrom && dateTo && (
        <DashboardCharts
          dateFrom={dateFrom}
          dateTo={dateTo}
          periodType={periodType}
        />
      )}
    </Box>
  );
}
